import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import axios from "../../services/api";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import PaymentNotification from "./Paymentnotification";
import "./styles/AllMemories.css";

function AllMemories() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 12;

  const [stats, setStats] = useState({
    totalProfiles: 0,
    totalMartyrs: 0,
    averageAge: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);

  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  const [search, setSearch] = useState({
    firstName: "",
    lastName: "",
    fatherName: ""
  });
  const [submittedSearch, setSubmittedSearch] = useState(search);

  const azMonthNames = useMemo(() => [
    "yanvar", "fevral", "mart", "aprel", "may", "iyun",
    "iyul", "avqust", "sentyabr", "oktyabr", "noyabr", "dekabr"
  ], []);

  const formatAzDate = useCallback((dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = azMonthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }, [azMonthNames]);

  const formatShortDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  }, []);

  const calculateAge = useCallback((birth, death) => {
    const dob = new Date(birth);
    const dod = new Date(death);
    let age = dod.getFullYear() - dob.getFullYear();
    const m = dod.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && dod.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  }, []);

  const timeSinceDeath = useCallback((deathDate) => {
    const now = new Date();
    const death = new Date(deathDate);
    const diffTime = Math.abs(now - death);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffYears = Math.floor(diffDays / 365);
    const diffMonths = Math.floor((diffDays % 365) / 30);

    if (diffYears > 0) return `${diffYears} il`;
    if (diffMonths > 0) return `${diffMonths} ay`;
    if (diffDays > 7) return `${Math.floor(diffDays / 7)} həftə`;
    return `${diffDays} gün`;
  }, []);

  const capitalizeWords = useCallback((text) => {
    if (!text) return "";
    return text
      .toLowerCase()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, []);

  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const { data } = await axios.get("/memorialprofile/stats");
      setStats({
        totalProfiles: data?.totalProfiles ?? 0,
        totalMartyrs: data?.totalMartyrs ?? 0,
        averageAge: data?.averageAge ?? 0
      });
    } catch (e) {
      console.error("Stats yüklənmədi:", e);
      setStats({ totalProfiles: 0, totalMartyrs: 0, averageAge: 0 });
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadProfilesWithNewParams = useCallback(async (searchParams, filter, sort) => {
    try {
      setLoading(true);
      const params = { ...searchParams, page: 1, pageSize };
      if (filter && filter !== 'all') params.filter = filter;
      if (sort && sort !== 'newest') params.sortBy = sort;

      const res = await axios.get("/memorialprofile/filter", { params });
      const newProfiles = res.data.Items || res.data.items || [];
      setProfiles(newProfiles);
      setHasMore(newProfiles.length === pageSize);
    } catch (err) {
      console.error("Yükləmə xətası:", err);
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const loadProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const params = { ...submittedSearch, page, pageSize };
      if (activeFilter && activeFilter !== 'all') params.filter = activeFilter;
      if (sortBy && sortBy !== 'newest') params.sortBy = sortBy;

      const res = await axios.get("/memorialprofile/filter", { params });
      const newProfiles = res.data.Items || res.data.items || [];

      if (page === 1) {
        setProfiles(newProfiles);
      } else {
        setProfiles(prev => [...prev, ...newProfiles]);
      }
      setHasMore(newProfiles.length === pageSize);
    } catch (err) {
      console.error("Profillər yüklənmədi:", err);
    } finally {
      setLoading(false);
    }
  }, [submittedSearch, activeFilter, sortBy, page, pageSize]);

  const observer = useRef();
  const lastProfileRef = useCallback((node) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prev => prev + 1);
      }
    }, { rootMargin: "100px", threshold: 0.1 });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  useEffect(() => {
    if (page > 1) loadProfiles();
  }, [page]);

  useEffect(() => { loadProfiles(); }, [activeFilter, sortBy, submittedSearch]);
  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    const initialLoad = async () => {
      try {
        setLoading(true);
        const params = { firstName: "", lastName: "", fatherName: "", page: 1, pageSize };
        const res = await axios.get("/memorialprofile/filter", { params });
        const newProfiles = res.data.Items || res.data.items || [];
        setProfiles(newProfiles);
        setHasMore(newProfiles.length === pageSize);
      } catch (err) {
        console.error("İlk yükləmə xətası:", err);
      } finally {
        setLoading(false);
      }
    };
    initialLoad();
  }, []);

  const handleSearchChange = useCallback((e) => {
    const { name, value } = e.target;
    setSearch(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSearch = useCallback(async (e) => {
    e.preventDefault();
    try {
      setProfiles([]);
      setPage(1);
      setHasMore(true);
      setLoading(true);

      const params = { ...search, page: 1, pageSize };
      if (activeFilter && activeFilter !== 'all') params.filter = activeFilter;
      if (sortBy && sortBy !== 'newest') params.sortBy = sortBy;

      const res = await axios.get("/memorialprofile/filter", { params });
      const newProfiles = res.data.Items || res.data.items || [];
      setProfiles(newProfiles);
      setHasMore(newProfiles.length === pageSize);
      setSubmittedSearch(search);
    } catch (err) {
      console.error("Axtarış xətası:", err);
    } finally {
      setLoading(false);
    }
  }, [search, activeFilter, sortBy, pageSize]);

  const handleFilterChange = useCallback((newFilter) => {
    setActiveFilter(newFilter);
    setProfiles([]);
    setPage(1);
    setHasMore(true);
    setTimeout(() => {
      loadProfilesWithNewParams(submittedSearch, newFilter, sortBy);
    }, 100);
  }, [submittedSearch, sortBy, loadProfilesWithNewParams]);

  const handleSortChange = useCallback((newSort) => {
    setSortBy(newSort);
    setProfiles([]);
    setPage(1);
    setHasMore(true);
    setTimeout(() => {
      loadProfilesWithNewParams(submittedSearch, activeFilter, newSort);
    }, 100);
  }, [submittedSearch, activeFilter, loadProfilesWithNewParams]);

  const hasActiveSearch = search.firstName || search.lastName || search.fatherName;

  const profileCards = useMemo(() => {
    return profiles.map((profile, idx) => {
      const isLast = profiles.length === idx + 1;
      const age = calculateAge(profile.dateOfBirth, profile.dateOfDeath);
      const timeAgo = timeSinceDeath(profile.dateOfDeath);

      return (
        <Link
          to={`/merhum/${profile.id}`}
          key={profile.id}
          className={`am-card ${profile.isMartyr ? "am-card--martyr" : ""}`}
          ref={isLast ? lastProfileRef : null}
          style={{ '--delay': `${(idx % 12) * 0.05}s` }}
        >
          <div className="am-card__img">
            <img
              src={profile.photoUrl ? `https://api.heyatyolu.az${profile.photoUrl}` : "/placeholder.jpg"}
              alt={`${profile.firstName} ${profile.lastName}`}
              loading="lazy"
              onError={(e) => { e.target.src = "/placeholder.jpg"; }}
            />
            <div className="am-card__badges">
              <span className="am-card__time">{timeAgo}</span>
              {profile.isMartyr && (
                <span className="am-card__martyr">🇦🇿 Şəhid</span>
              )}
            </div>
          </div>

          <div className="am-card__body">
            <h3 className="am-card__name">
              {capitalizeWords(`${profile.firstName} ${profile.fatherName} ${profile.lastName}`)}
            </h3>

            <div className="am-card__dates">
              <span>{formatShortDate(profile.dateOfBirth)}</span>
              <span className="am-card__age">{age}</span>
              <span>{formatShortDate(profile.dateOfDeath)}</span>
            </div>

            {profile.birthPlace && (
              <p className="am-card__location">
                <i className="fas fa-map-marker-alt"></i> {profile.birthPlace}
              </p>
            )}
          </div>
        </Link>
      );
    });
  }, [profiles, lastProfileRef, capitalizeWords, formatShortDate, calculateAge, timeSinceDeath]);

  useEffect(() => {
    return () => { if (observer.current) observer.current.disconnect(); };
  }, []);

  const isFirstLoad = page === 1 && profiles.length === 0 && loading;

  return (
    <div className="am-page">
      <PaymentNotification />
      <Helmet>
        <title>Bütün Xatirələr – HeyatYolu</title>
        <meta name="description" content="Bütün mərhum profillərinə baxın. Həyatdan köçmüş sevdiklərinizi yad edin və onların xatirəsini paylaşın." />
        <meta name="keywords" content="xatirə profilləri, mərhumlar, şəhidlər, yaddaş, vəfat etmişlər, online yas, rəqəmsal xatirə, heyatyolu" />
        <meta property="og:title" content="Bütün Xatirələr – HeyatYolu" />
        <meta property="og:description" content="Bütün mərhum profillərinə baxın. Həyatdan köçmüş sevdiklərinizi yad edin və paylaşın." />
        <meta property="og:image" content="https://api.heyatyolu.az/favicon.ico" />
        <meta property="og:url" content="https://api.heyatyolu.az/butun-xatireler" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* HEADER */}
      <header className="am-header">
        <div className="am-header__inner">
          <h1 className="am-header__title">
            <span className="am-header__icon">🕊️</span>
            Həyat Yolunda Qalan İzlər
          </h1>
          <p className="am-header__sub">
            Hər bir xatirə, yaşanmış bir həyatın əbədi tanığıdır
          </p>

          <div className="am-stats">
            {!statsLoading ? (
              <>
                <div className="am-stats__item">
                  <strong>{stats.totalProfiles.toLocaleString()}</strong>
                  <span>xatirə</span>
                </div>
                <div className="am-stats__divider"></div>
                <div className="am-stats__item">
                  <strong>{stats.totalMartyrs.toLocaleString()}</strong>
                  <span>şəhid</span>
                </div>
                <div className="am-stats__divider"></div>
                <div className="am-stats__item">
                  <strong>{Math.round(stats.averageAge)}</strong>
                  <span>orta yaş</span>
                </div>
              </>
            ) : (
              <div className="am-stats__loading">Yüklənir...</div>
            )}
          </div>
        </div>
      </header>

      {/* SEARCH & FILTERS - Compact */}
      <section className="am-toolbar">
        <form onSubmit={handleSearch} className="am-search">
          <div className="am-search__main">
            <i className="fas fa-search am-search__icon"></i>
            <input
              type="text"
              name="firstName"
              placeholder="Ad ilə axtar..."
              value={search.firstName}
              onChange={handleSearchChange}
              className="am-search__input"
            />
            <button
              type="button"
              className={`am-search__toggle ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
              title="Ətraflı axtarış"
            >
              <i className="fas fa-sliders-h"></i>
            </button>
            <button type="submit" className="am-search__btn" disabled={loading}>
              {loading ? <i className="fas fa-spinner fa-spin"></i> : "Axtar"}
            </button>
          </div>

          {/* Expandable detailed search */}
          {showFilters && (
            <div className="am-search__expanded">
              <div className="am-search__fields">
                <input
                  type="text"
                  name="fatherName"
                  placeholder="Ata adı..."
                  value={search.fatherName}
                  onChange={handleSearchChange}
                />
                <input
                  type="text"
                  name="lastName"
                  placeholder="Soyad..."
                  value={search.lastName}
                  onChange={handleSearchChange}
                />
              </div>

              <div className="am-search__options">
                <div className="am-filters">
                  <button
                    type="button"
                    className={`am-filter ${activeFilter === 'all' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('all')}
                  >Hamısı</button>
                  <button
                    type="button"
                    className={`am-filter ${activeFilter === 'martyrs' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('martyrs')}
                  >🇦🇿 Şəhidlər</button>
                  <button
                    type="button"
                    className={`am-filter ${activeFilter === 'recent' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('recent')}
                  >Son zamanlar</button>
                  <button
                    type="button"
                    className={`am-filter ${activeFilter === 'elderly' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('elderly')}
                  >Yaşlı nəsil</button>
                </div>

                <select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="am-sort"
                >
                  <option value="newest">Ən yeni</option>
                  <option value="oldest">Ən köhnə</option>
                  <option value="name">Ada görə</option>
                  <option value="age_young">Gəncdən yaşlıya</option>
                  <option value="age_old">Yaşlıdan gəncə</option>
                </select>
              </div>
            </div>
          )}
        </form>

        {/* Active filter chips */}
        {(activeFilter !== 'all' || hasActiveSearch) && (
          <div className="am-active-filters">
            {activeFilter !== 'all' && (
              <span className="am-chip">
                {activeFilter === 'martyrs' ? '🇦🇿 Şəhidlər' :
                  activeFilter === 'recent' ? 'Son zamanlar' : 'Yaşlı nəsil'}
                <button onClick={() => handleFilterChange('all')}>×</button>
              </span>
            )}
            {hasActiveSearch && (
              <span className="am-chip">
                Axtarış aktiv
                <button onClick={() => {
                  setSearch({ firstName: "", lastName: "", fatherName: "" });
                  setSubmittedSearch({ firstName: "", lastName: "", fatherName: "" });
                  setPage(1);
                  setProfiles([]);
                  loadProfilesWithNewParams({ firstName: "", lastName: "", fatherName: "" }, activeFilter, sortBy);
                }}>×</button>
              </span>
            )}
          </div>
        )}
      </section>

      {/* RESULTS */}
      <section className="am-results">
        {profiles.length === 0 && !loading ? (
          <div className="am-empty">
            <div className="am-empty__icon">🕊️</div>
            <h3>Xatirə tapılmadı</h3>
            <p>Başqa adlarla axtarmağı yoxlayın</p>
            <button
              onClick={() => {
                setSearch({ firstName: "", lastName: "", fatherName: "" });
                setActiveFilter('all');
                handleFilterChange('all');
              }}
              className="am-empty__btn"
            >
              Hamısını göstər
            </button>
          </div>
        ) : isFirstLoad ? (
          <div className="am-grid">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="am-skeleton">
                <div className="am-skeleton__img"></div>
                <div className="am-skeleton__body">
                  <div className="am-skeleton__line am-skeleton__line--long"></div>
                  <div className="am-skeleton__line am-skeleton__line--medium"></div>
                  <div className="am-skeleton__line am-skeleton__line--short"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="am-grid">
            {profileCards}
          </div>
        )}
      </section>

      {/* LOADING MORE */}
      {loading && !isFirstLoad && (
        <div className="am-loading-more">
          <div className="am-loading-more__spinner"></div>
          <span>Daha çox yüklənir...</span>
        </div>
      )}

      {/* END MESSAGE */}
      {profiles.length > 0 && !hasMore && (
        <div className="am-end">
          <span className="am-end__candle">🕯️</span>
          <p>Xatirələrdə yaşayanlar heç vaxt ölməzlər</p>
        </div>
      )}
    </div>
  );
}

export default AllMemories;